import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ENABLE_REQUEST_AGENT } from '../../config/features';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import {
  Activity, AlertTriangle, CheckCircle2, ListTodo,
  Search, SlidersHorizontal, ChevronRight, Plus, Rocket,
  Loader2, GitBranch, Star, Lock, ExternalLink,
} from 'lucide-react';
import { agentsApi } from '../../services/agentsApi';
import { getAgentBuilderStatus } from '../../services/api';
import { getValidxBridgeUrl } from '../../utils/validxBridge';
import type { Project } from '../../types/agents';

// ── Status configuration ────────────────────────────────────────────

interface StatusConfig {
  label: string;
  dotClass: string;
  badgeClass: string;
  pulse?: boolean;
}

const STATUS_MAP: Record<string, StatusConfig> = {
  PENDING: {
    label: 'Queued',
    dotClass: 'bg-gray-400',
    badgeClass: 'bg-gray-50 text-gray-600 ring-gray-200',
  },
  REQUESTED: {
    label: 'Queued',
    dotClass: 'bg-gray-400',
    badgeClass: 'bg-gray-50 text-gray-600 ring-gray-200',
  },
  IN_PROGRESS: {
    label: 'Working',
    dotClass: 'bg-brand-500',
    badgeClass: 'bg-brand-50 text-brand-600 ring-brand-200',
    pulse: true,
  },
  IN_DEVELOPMENT: {
    label: 'In Development',
    dotClass: 'bg-brand-500',
    badgeClass: 'bg-brand-50 text-brand-600 ring-brand-200',
    pulse: true,
  },
  TESTING: {
    label: 'Testing',
    dotClass: 'bg-amber-400',
    badgeClass: 'bg-amber-50 text-amber-700 ring-amber-200',
    pulse: true,
  },
  COMPLETED: {
    label: 'Complete',
    dotClass: 'bg-gray-900',
    badgeClass: 'bg-gray-100 text-gray-900 ring-gray-300',
  },
  LIVE: {
    label: 'Live',
    dotClass: 'bg-brand-500',
    badgeClass: 'bg-brand-50 text-brand-700 ring-brand-200',
  },
  FAILED: {
    label: 'Investigating',
    dotClass: 'bg-red-500',
    badgeClass: 'bg-red-50 text-red-700 ring-red-200',
  },
  CANCELLED: {
    label: 'Cancelled',
    dotClass: 'bg-gray-300',
    badgeClass: 'bg-gray-50 text-gray-500 ring-gray-200',
  },
  TEMPLATE: {
    label: 'Repository',
    dotClass: 'bg-gray-700',
    badgeClass: 'bg-gray-100 text-gray-700 ring-gray-300',
  },
};

function getStatusConfig(status: string): StatusConfig {
  return STATUS_MAP[status] || STATUS_MAP.PENDING;
}

function getDisplayStatus(project: Project): string {
  if (project.latestDisplay?.status) return project.latestDisplay.status;
  return getStatusConfig(project.status).label;
}

// ── Helpers ──────────────────────────────────────────────────────────

function formatDomain(domain?: string | null): { label: string; color: string } {
  if (!domain) return { label: '—', color: 'text-gray-400' };
  const map: Record<string, { label: string; color: string }> = {
    CODE: { label: 'Code', color: 'text-brand-500' },
    LEGAL: { label: 'Legal', color: 'text-amber-600' },
    MEDICAL: { label: 'Medical', color: 'text-red-600' },
    BUSINESS: { label: 'Business', color: 'text-brand-500' },
    CREATIVE: { label: 'Creative', color: 'text-gray-600' },
    TECHNICAL: { label: 'Technical', color: 'text-cyan-600' },
  };
  return map[domain] || { label: domain.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), color: 'text-gray-600' };
}

function timeAgo(dateStr: string): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

// ── Stats Cards ─────────────────────────────────────────────────────

function StatsCards({ projects }: { projects: Project[] }) {
  const real = projects.filter(v => v.status !== 'TEMPLATE');
  const active = real.filter(v =>
    ['IN_PROGRESS', 'IN_DEVELOPMENT', 'TESTING'].includes(v.status)
  ).length;
  const attention = real.filter(v =>
    v.status === 'FAILED' || v.latestDisplay?.category === 'attention'
  ).length;
  const completed = real.filter(v =>
    ['COMPLETED', 'LIVE'].includes(v.status)
  ).length;
  const totalTickets = real.reduce((sum, v) => sum + (v.ticketCount || 0), 0);
  const completedTickets = real.reduce((sum, v) => sum + (v.completedTickets || 0), 0);
  const repoCount = projects.filter(v => v.status === 'TEMPLATE').length;

  const stats = [
    { label: 'Active Tasks', value: active, Icon: Activity, color: 'text-brand-500', bg: 'bg-brand-50' },
    { label: 'Needs Attention', value: attention, Icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-50' },
    { label: 'Completed', value: completed, Icon: CheckCircle2, color: 'text-gray-700', bg: 'bg-gray-100' },
    { label: 'Total Tickets', value: totalTickets, Icon: ListTodo, color: 'text-brand-500', bg: 'bg-brand-50' },
    ...(repoCount > 0
      ? [{ label: 'Repositories', value: repoCount, Icon: GitBranch, color: 'text-gray-700', bg: 'bg-gray-100' }]
      : [{ label: 'Tickets Done', value: completedTickets, Icon: CheckCircle2, color: 'text-gray-500', bg: 'bg-gray-50' }]),
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
      {stats.map(({ label, value, Icon, color, bg }) => (
        <div
          key={label}
          className="bg-white rounded-xl border border-gray-100 px-4 py-4 flex items-center gap-3"
        >
          <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center shrink-0`}>
            <Icon className={`w-5 h-5 ${color}`} strokeWidth={1.5} />
          </div>
          <div className="min-w-0">
            <div className="text-2xl font-bold text-gray-900 leading-tight">{value}</div>
            <div className="text-xs text-gray-500 truncate">{label}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Ticket Progress ─────────────────────────────────────────────────

function TicketProgress({ project }: { project: Project }) {
  const total = project.ticketCount || 0;
  const done = project.completedTickets || 0;
  const active = project.activeTickets || 0;

  if (total === 0) return <span className="text-sm text-gray-400">—</span>;

  // Small counts: individual bars (up to 24)
  if (total <= 24) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-[2px]">
          {Array.from({ length: total }, (_, i) => {
            let color = 'bg-gray-200';
            if (i < done) color = 'bg-gray-900';
            else if (i < done + active) color = 'bg-brand-400';
            return <div key={i} className={`w-[3px] h-4 rounded-[1px] ${color}`} />;
          })}
        </div>
        <span className="text-xs text-gray-500 tabular-nums font-medium">{done}/{total}</span>
      </div>
    );
  }

  // Large counts: progress bar with percentage
  const pct = Math.round((done / total) * 100);
  const activePct = Math.round((active / total) * 100);

  return (
    <div className="flex items-center gap-2.5 min-w-0">
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden min-w-[60px] max-w-[100px]">
        <div className="h-full flex">
          {pct > 0 && <div className="bg-gray-900 h-full" style={{ width: `${pct}%` }} />}
          {activePct > 0 && <div className="bg-brand-400 h-full" style={{ width: `${activePct}%` }} />}
        </div>
      </div>
      <span className="text-xs text-gray-500 tabular-nums font-medium whitespace-nowrap">{done}/{total}</span>
    </div>
  );
}

// ── Status Badge ────────────────────────────────────────────────────

function StatusBadge({ project }: { project: Project }) {
  const cfg = getStatusConfig(project.status);
  const label = getDisplayStatus(project);
  const isExpertReview = project.latestDisplay?.category === 'attention';
  const badgeCls = isExpertReview
    ? 'bg-amber-50 text-amber-700 ring-amber-200'
    : cfg.badgeClass;
  const dotCls = isExpertReview ? 'bg-amber-500' : cfg.dotClass;

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ring-1 ring-inset whitespace-nowrap ${badgeCls}`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${dotCls} ${cfg.pulse ? 'animate-pulse' : ''}`}
      />
      {isExpertReview ? 'Expert Review' : label}
    </span>
  );
}

// ── Pipeline Phase ──────────────────────────────────────────────────

const PIPELINE_LABELS: Record<string, { label: string; color: string }> = {
  PRD: { label: 'PRD', color: 'text-violet-600 bg-violet-50' },
  BLUEPRINT: { label: 'Blueprint', color: 'text-blue-600 bg-blue-50' },
  TASKS: { label: 'Planning', color: 'text-cyan-600 bg-cyan-50' },
  EXECUTING: { label: 'Building', color: 'text-brand-600 bg-brand-50' },
  REVIEWING: { label: 'Reviewing', color: 'text-amber-600 bg-amber-50' },
  DEPLOYING: { label: 'Deploying', color: 'text-green-600 bg-green-50' },
  COMPLETED: { label: 'Shipped', color: 'text-gray-700 bg-gray-100' },
  FAILED: { label: 'Failed', color: 'text-red-600 bg-red-50' },
};

function PipelinePhaseBadge({ phase }: { phase?: string | null }) {
  if (!phase) return null;
  const cfg = PIPELINE_LABELS[phase];
  if (!cfg) return null;
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

// ── Table Row ───────────────────────────────────────────────────────

function TableRow({ project, onClick, validxAppUrl }: { project: Project; onClick: () => void; validxAppUrl?: string | null }) {
  const isRepo = project.source === 'github' || project.id.startsWith('repo:');
  const activityMsg =
    project.latestDisplay?.message || project.latestActivity?.title || '';
  const activityTime = timeAgo(project.updatedAt || project.createdAt);
  const domain = formatDomain(project.domain);

  const hasBuilder = !!(validxAppUrl && !isRepo);

  return (
    <div
      onClick={onClick}
      className="grid grid-cols-[2.5fr_1fr_0.8fr_1.5fr_1.5fr] items-center px-5 py-4 border-b border-gray-50 last:border-b-0 hover:bg-gray-50/70 cursor-pointer transition-colors group"
    >
      {/* Agent */}
      <div className="min-w-0 pr-4">
        <div className="flex items-center gap-2">
          {isRepo && <GitBranch className="w-4 h-4 text-gray-500 shrink-0" />}
          <p className="text-sm font-medium text-gray-900 truncate group-hover:text-brand-600 transition-colors">
            {project.title}
          </p>
          {isRepo && project.repoMeta?.isPrivate && (
            <Lock className="w-3 h-3 text-gray-400 shrink-0" />
          )}
        </div>
        {isRepo && project.repoMeta ? (
          <p className="text-xs text-gray-400 truncate mt-1">{project.repoMeta.fullName}</p>
        ) : project.expert?.name ? (
          <div className="flex items-center gap-2 mt-1.5">
            <div className="w-5 h-5 rounded-full bg-brand-100 flex items-center justify-center text-[10px] font-semibold text-brand-600 shrink-0">
              {project.expert.name.charAt(0).toUpperCase()}
            </div>
            <span className="text-xs text-gray-500 truncate">{project.expert.name}</span>
          </div>
        ) : null}
      </div>

      {/* Status */}
      <div className="flex flex-col gap-1">
        <StatusBadge project={project} />
        <PipelinePhaseBadge phase={project.pipelinePhase} />
      </div>

      {/* Domain */}
      <div>
        {isRepo && project.repoMeta?.language ? (
          <span className="text-sm font-medium text-gray-600">{project.repoMeta.language}</span>
        ) : (
          <span className={`text-sm font-medium ${domain.color}`}>{domain.label}</span>
        )}
      </div>

      {/* Tickets / Stars */}
      <div>
        {isRepo ? (
          <div className="flex items-center gap-1.5 text-sm text-gray-500">
            <Star className="w-3.5 h-3.5 text-amber-400" />
            <span className="tabular-nums">{project.repoMeta?.stars || 0}</span>
          </div>
        ) : (
          <TicketProgress project={project} />
        )}
      </div>

      {/* Last Activity */}
      <div className="flex items-center justify-between min-w-0">
        <div className="min-w-0 mr-2">
          {isRepo ? (
            <p className="text-xs text-gray-400">Not deployed</p>
          ) : (
            <>
              {activityMsg && (
                <p className="text-sm text-gray-700 truncate">{activityMsg}</p>
              )}
              <p className="text-xs text-gray-400">{activityTime}</p>
            </>
          )}
        </div>
        {hasBuilder && (
          <button
            onClick={async (e) => {
              e.stopPropagation();
              const bridgeUrl = await getValidxBridgeUrl(
                validxAppUrl!,
                `/customer/project/${project.id}/builder/`,
                {},
              );
              window.open(bridgeUrl, '_blank', 'noopener,noreferrer');
            }}
            className="w-7 h-7 rounded-md flex items-center justify-center text-gray-300 hover:text-brand-600 hover:bg-brand-50 transition-colors opacity-0 group-hover:opacity-100 shrink-0"
            title="Open in Builder"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </button>
        )}
        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 shrink-0 transition-colors" />
      </div>
    </div>
  );
}

// ── Empty State ─────────────────────────────────────────────────────

function EmptyState({ onCreateClick }: { onCreateClick: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-100">
      <div className="w-16 h-16 rounded-2xl bg-brand-50 flex items-center justify-center mb-4">
        <Rocket className="w-7 h-7 text-brand-500" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">No agents yet</h3>
      <p className="text-sm text-gray-500 mb-6 text-center max-w-sm">
        {ENABLE_REQUEST_AGENT
          ? 'Get started by requesting your first AI agent. Describe what you need and our system will handle the rest.'
          : 'Your Agents Anywhere consultant sets up agents for you — new ones appear here as they go live.'}
      </p>
      {ENABLE_REQUEST_AGENT && (
        <button
          onClick={onCreateClick}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-black text-white rounded-xl text-sm font-medium hover:bg-gray-800 transition-colors shadow-sm whitespace-nowrap"
        >
          <Plus className="w-4 h-4" />
          Request New Agent
        </button>
      )}
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────

const AgentsManagement: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [abConnected, setAbConnected] = useState<boolean | null>(null);
  const [validxAppUrl, setValidxAppUrl] = useState<string | null>(null);

  // Check Agent Builder connection on mount
  useEffect(() => {
    getAgentBuilderStatus()
      .then(data => {
        setAbConnected(data.connected === true);
        if (data.validxAppUrl) setValidxAppUrl(data.validxAppUrl);
      })
      .catch(() => setAbConnected(false));
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const params: any = { limit: 50 };
      if (statusFilter) params.status = statusFilter;
      const { data } = await agentsApi.listProjects(params);

      let parsed: Project[] = [];
      if (data?.content?.[0]?.text) {
        try {
          const inner = JSON.parse(data.content[0].text);
          parsed = inner.projects || inner || [];
        } catch {
          parsed = [];
        }
      } else if (Array.isArray(data)) {
        parsed = data;
      }
      setProjects(parsed);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    if (abConnected !== true) return; // Only fetch when confirmed connected
    setLoading(true);
    fetchProjects();
    const interval = setInterval(fetchProjects, 15000);
    return () => clearInterval(interval);
  }, [fetchProjects, abConnected]);

  // Client-side search filter
  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return projects;
    const q = searchQuery.toLowerCase();
    return projects.filter(
      v =>
        v.title.toLowerCase().includes(q) ||
        v.description?.toLowerCase().includes(q) ||
        v.expert?.name?.toLowerCase().includes(q) ||
        v.domain?.toLowerCase().includes(q) ||
        v.repoMeta?.fullName?.toLowerCase().includes(q) ||
        v.repoMeta?.language?.toLowerCase().includes(q)
    );
  }, [projects, searchQuery]);

  // Show loading while checking connection
  if (abConnected === null) {
    return (
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">AI Agents</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage and monitor your agent workspaces
          </p>
        </div>
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 text-brand-500 animate-spin" />
        </div>
      </div>
    );
  }

  // Show provisioning prompt if not connected
  if (abConnected === false) {
    return (
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">AI Agents</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage and monitor your agent workspaces
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center max-w-lg mx-auto mt-12">
          <div className="w-14 h-14 rounded-2xl bg-brand-50 flex items-center justify-center mx-auto mb-4">
            <Rocket className="w-7 h-7 text-brand-500" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Connect Agent Builder</h2>
          <p className="text-sm text-gray-500 mb-6">
            To start using AI Agents, connect your account to Agent Builder first. This sets up your workspace and enables agent execution.
          </p>
          <button
            onClick={() => navigate('/consultant/settings')}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-black text-white rounded-xl text-sm font-medium hover:bg-gray-800 transition-all shadow-sm whitespace-nowrap"
          >
            Go to Settings
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Page header */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Agents</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage and monitor your agent workspaces
          </p>
        </div>
        {ENABLE_REQUEST_AGENT && (
          <button
            onClick={async () => {
              // New agents are created in the validx builder (the one
              // workspace). Bridge into a fresh project there.
              if (!validxAppUrl) { toast.info('Connect the builder to create agents.'); return; }
              const bridgeUrl = await getValidxBridgeUrl(
                validxAppUrl,
                `/customer/project/new/builder/`,
                {},
              );
              window.open(bridgeUrl, '_blank', 'noopener,noreferrer');
            }}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-black text-white rounded-xl text-sm font-medium hover:bg-gray-800 transition-all shadow-sm hover:shadow-md whitespace-nowrap"
          >
            <Plus className="w-4 h-4" />
            Request New Agent
          </button>
        )}
      </div>

      {/* Stats Cards */}
      {projects.length > 0 && <StatsCards projects={projects} />}

      {/* Search + Filter bar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by title, customer, company."
              className="pl-9 pr-4 py-2 w-72 border border-gray-200 rounded-lg text-sm bg-white text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 transition-all"
            />
          </div>
          <button
            className="inline-flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors whitespace-nowrap"
            onClick={() => {
              /* future: open filter panel */
            }}
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            Filters
          </button>
        </div>
        <span className="text-sm text-gray-400">
          {filtered.length} agent{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Content */}
      {loading && projects.length === 0 ? (
        <div className="flex items-center justify-center py-20 bg-white rounded-xl border border-gray-100">
          <div className="flex items-center gap-3 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">Loading your agents...</span>
          </div>
        </div>
      ) : filtered.length === 0 && projects.length === 0 ? (
        <EmptyState onCreateClick={async () => {
          if (!validxAppUrl) { toast.info('Connect the builder to create agents.'); return; }
          const bridgeUrl = await getValidxBridgeUrl(
            validxAppUrl,
            `/customer/project/new/builder/`,
            {},
          );
          window.open(bridgeUrl, '_blank', 'noopener,noreferrer');
        }} />
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <div className="min-w-[800px]">
              {/* Table Header */}
              <div className="grid grid-cols-[2.5fr_1fr_0.8fr_1.5fr_1.5fr] px-5 py-3 border-b border-gray-100">
                <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                  Agent
                </span>
                <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                  Status
                </span>
                <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                  Domain
                </span>
                <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                  Tickets
                </span>
                <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                  Last Activity
                </span>
              </div>

              {/* Table Body */}
              {filtered.map(v => {
                const isRepo = v.source === 'github' || v.id.startsWith('repo:');
                const hasBuilderLink = !!(validxAppUrl && !isRepo);
                return (
                  <TableRow
                    key={v.id}
                    project={v}
                    onClick={async () => {
                      // The workspace lives on validx — open via the HMAC
                      // bridge. The in-app workspace viewer was retired.
                      if (hasBuilderLink) {
                        const bridgeUrl = await getValidxBridgeUrl(
                          validxAppUrl!,
                          `/customer/project/${v.id}/builder/`,
                          {},
                        );
                        window.open(bridgeUrl, '_blank', 'noopener,noreferrer');
                      } else {
                        toast.info('Open this agent from the builder workspace.');
                      }
                    }}
                    validxAppUrl={validxAppUrl}
                  />
                );
              })}

              {/* No search results */}
              {filtered.length === 0 && projects.length > 0 && (
                <div className="px-5 py-12 text-center text-sm text-gray-400">
                  No agents match your search.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AgentsManagement;
